import React, { useEffect, useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import clsx from "clsx";
import { Controller, useForm } from "react-hook-form";
import styles from "./index.module.css";
import { assetFlagsSchema } from "../../yup";
import { Button } from "react-bootstrap";
import { convertFormula, revertFormula } from "../../utils/assetColumnFormula";
import { createAssetFlag, updateAssetFlag } from "../../service/api";
import { showNotification } from "../../service/showNotification";
import { tracker } from "../../service/mixpanel";

// export const allowedFunctions = ["IF", "AND", "OR"];
export const keywordsToHighlight = ["IF", "AND", "OR", "<", ">", "=="];

const TextInputCell = ({
  name,
  control,
  error,
  placeholder,
  disabled = false,
  width,
}) => (
  <td>
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <input
          {...field}
          value={field.value || ""}
          placeholder={placeholder}
          disabled={disabled}
          className={styles.inputField}
          style={{
            border: error ? "1px solid red" : "1px solid #ccc",
            backgroundColor: disabled ? "#f8f9fa" : "white",
            cursor: disabled ? "not-allowed" : "text",
            width: width,
          }}
        />
      )}
    />
    {error && (
      <div style={{ color: "red", fontSize: "12px" }}>{error.message}</div>
    )}
  </td>
);

// Enhanced Formula Input Cell with autocomplete
const FormulaInputCell = ({
  name,
  control,
  error,
  disabled,
  formulaOptions,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const inputRef = useRef(null);
  const highlightRef = useRef(null);

  const highlightFormula = (value) => {
    if (!value) return "";
    let highlighted = value;

    keywordsToHighlight.forEach((kw) => {
      const regex = new RegExp(`\\b${kw}\\b`, "gi");
      highlighted = highlighted.replace(
        regex,
        (match) => `<span class="${styles.highlight}">${match}</span>`
      );
    });

    return highlighted;
  };

  const handleFormulaChange = (value, onChange) => {
    onChange(value);

    if (highlightRef.current) {
      highlightRef.current.innerHTML = highlightFormula(value);
    }

    if (disabled) return;

    const input = inputRef.current;
    if (!input) return;

    const cursor = input.selectionStart;
    const hashIndex = value.lastIndexOf("#", cursor - 1);
    const afterHash = value.slice(hashIndex + 1, cursor);

    if (hashIndex !== -1 && !/\s/.test(afterHash)) {
      const matches = formulaOptions?.filter((opt) =>
        opt.label.toLowerCase().includes(afterHash.toLowerCase())
      );
      setFilteredOptions(matches);
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  };

  const handleSelect = (option, currentValue, onChange) => {
    const input = inputRef.current;
    if (!input) return;

    const cursor = input.selectionStart;
    const hashIndex = currentValue.lastIndexOf("#", cursor - 1);
    const before = currentValue.slice(0, hashIndex);
    const after = currentValue.slice(cursor);
    const newValue = `${before}#${option.label}${after}`;

    onChange(newValue);
    setShowDropdown(false);
    input.focus();
  };

  const handleScroll = (e) => {
    if (highlightRef.current && inputRef.current) {
      highlightRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if (inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, []);

  return (
    <td>
      <div style={{ position: "relative" }}>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <>
              <div
                ref={highlightRef}
                className={styles.highlightLayer}
                dangerouslySetInnerHTML={{
                  __html: highlightFormula(field.value || ""),
                }}
              />
              <input
                {...field}
                ref={inputRef}
                value={field.value || ""}
                onChange={(e) =>
                  handleFormulaChange(e.target.value, field.onChange)
                }
                onScroll={handleScroll} // Add this
                placeholder="Enter flag (type # to use column names)"
                disabled={disabled}
                className={clsx(styles.formulaInput, styles.inputField)}
                style={{
                  border: error ? "1px solid red" : "1px solid #ccc",
                  backgroundColor: disabled ? "#f8f9fa" : "white",
                  cursor: disabled ? "not-allowed" : "text",
                  color: "transparent", // hide actual text
                  caretColor: "black", // keep caret visible
                }}
                autoComplete="off"
              />

              {showDropdown && !disabled && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "white",
                    border: "1px solid #ccc",
                    borderTop: "none",
                    maxHeight: "150px",
                    overflowY: "auto",
                    zIndex: 20,
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  {filteredOptions.length === 0 ? (
                    <div style={{ padding: "8px", color: "#666" }}>
                      No matches
                    </div>
                  ) : (
                    filteredOptions.map((opt) => (
                      <div
                        key={opt.value}
                        style={{
                          padding: "8px",
                          cursor: "pointer",
                          borderBottom: "1px solid #eee",
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.backgroundColor = "#f8f9fa")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.backgroundColor = "white")
                        }
                        onClick={() =>
                          handleSelect(opt, field.value, field.onChange)
                        }
                      >
                        {opt.label}
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        />
      </div>
      {error && (
        <div style={{ color: "red", fontSize: "12px" }}>{error.message}</div>
      )}
    </td>
  );
};

const AssetFlagForm = ({
  assetColumns,
  rowData,
  fetchAssetFlags,
  onDelete,
  setIsNewUploaded,
}) => {
  const formulaOptions =
    assetColumns
      ?.filter((item) => !item?.isInternal && !item?.isDocument) // no internal column or document column
      ?.map((item) => ({
        label: item?.label.replaceAll(" ", "_"),
        value: item?._id,
      })) || [];

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(assetFlagsSchema),
    defaultValues: {
      name: rowData?.name,
      flag: revertFormula(rowData?.flag, formulaOptions),
      description: rowData?.description,
    },
  });

  const trimQuotedText = (input) => {
    return input.replace(/"([^"]*)"/g, (_, inner) => `"${inner.trim()}"`);
  };

  const onFormSubmit = async (values) => {
    const isNew = rowData?._id?.toLowerCase()?.includes("new-");
    tracker.assetV2Events.assetFlagAddNewFlag();

    const formulaObj = convertFormula(values?.flag, formulaOptions);
    if (formulaObj) {
      values.flag = formulaObj?.convertedFormula;
      values.dependencies = formulaObj?.dependencies;
    }
    values.flag = trimQuotedText(values.flag);

    try {
      const res = isNew
        ? await createAssetFlag(values)
        : await updateAssetFlag(rowData?._id, values);
      fetchAssetFlags();
      setIsNewUploaded((prev) => !prev);
      showNotification(
        "success",
        res?.message ||
          (rowData?._id ? "Flag updated successfully." : "New flag created.")
      );
    } catch (err) {
      console.log("err", err);
      showNotification(
        "error",
        err?.response?.data?.message ||
          `Failed to ${rowData?._id ? "update" : "create"} new flag.`
      );
    }
  };

  return (
    <>
      <TextInputCell
        name="name"
        control={control}
        error={errors.name}
        placeholder="Flag name"
      />

      <FormulaInputCell
        name="flag"
        control={control}
        error={errors.flag}
        formulaOptions={formulaOptions}
      />

      <TextInputCell
        name="description"
        control={control}
        error={errors.description}
        placeholder="Description"
        width={"400px"}
      />

      <td className={styles.actionRow}>
        <Button
          type="button"
          onClick={handleSubmit(onFormSubmit)}
          disabled={isSubmitting}
          variant="success"
          size="sm"
          style={{ marginRight: "8px" }}
        >
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
        <Button type="button" onClick={onDelete} size="sm" variant="danger">
          Delete
        </Button>
      </td>
    </>
  );
};

export default AssetFlagForm;
